<script setup lang="ts">
import { computed } from 'vue'
import { useTranslate } from '@tolgee/vue'

import type { ImpactColor, ImpactType } from '@/api/interfaces'
import { formatTotalAmounts } from '@/helpers/utils'
import type { IconStyleType } from '@/api/interfaces'
import type { ComparisonType } from '@/stores/interfaces/PublicData'
import { useEditorStore } from '@/stores/Editor'

import ComparisonIcon from '@/components/icons/comparisons/ComparisonIcon.vue'
import { usePublicData } from '@/stores/PublicData'

interface Props {
  amount: { suffix?: string; value: number }
  comparisonType: ComparisonType
  impactType: ImpactType
  iconStyle: IconStyleType
  colorSettings: ImpactColor
}

const props = defineProps<Props>()

const editorStore = useEditorStore()
const publicStore = usePublicData()
const { t } = useTranslate()

const formattedValues = computed(() => {
  return formatTotalAmounts(props.amount.value, props.amount.suffix)
})

function tc(key: string, params?: Record<string, string | number>) {
  return t.value(`PublicComparisonCard.${props.impactType}.${props.comparisonType}.${key}`, params)
}

const link = computed(() => {
  return (
    publicStore.comparisons.find(
      (comparison) =>
        comparison.unit === props.comparisonType && comparison.type == props.impactType,
    )?.link || ''
  )
})
</script>

<template>
  <div
    :class="['public-comparison-card', editorStore.editor.icon.style]"
    :style="[{ backgroundColor: colorSettings.backgroundColor, color: colorSettings.textColor }]"
  >
    <div class="public-comparison-card-icon-wrapper">
      <ComparisonIcon
        class="public-comparison-card-icon"
        :icon-style="editorStore.editor.icon.style"
        :comparison-type="comparisonType"
        :impact-type="impactType"
      />
    </div>
    <div class="public-comparison-card-content">
      <div class="public-comparison-card-heading">
        <strong class="public-comparison-card-amount">
          {{ formattedValues }}
        </strong>
        <p class="public-comparison-card-description">
          {{ tc('desc', { value: props.amount.value }) }}
        </p>
      </div>
      <a :href="link" target="_blank" class="public-comparison-card-link">
        {{ tc('link') }}
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.public-comparison-card {
  position: relative;
  padding: 24px 32px;
  border-radius: 20px;
  color: var(--ui-black);

  &.simple {
    .public-comparison-card-icon {
      height: 50px;
      width: 50px;
    }
  }

  .community-page & {
    background-color: rgba(250, 250, 250, 0.1) !important;
    border: 1px solid #fafafa;
    border-radius: 10px;
    backdrop-filter: blur(10px);
  }
}

.public-comparison-card-amount {
  font-size: 45px;
  display: block;
  margin-bottom: 8px;

  @include media('sm-and-up') {
    font-size: 55px;
    margin-bottom: 16px;
  }
}

.public-comparison-card-description {
  font-size: 18px;
  margin-bottom: 12px;

  @include media('sm-and-up') {
    font-size: 24px;
    margin-bottom: 16px;
  }
}

.public-comparison-card-link {
  font-size: 12px;
  text-decoration: underline;
  color: inherit;
}

.public-comparison-card-icon-wrapper {
  margin-bottom: 16px;
}
</style>
